// frontend/src/components/crm/Leads.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { leadAPI, usersAPI, clientAPI, audiencesAPI } from '../../utils/crmApi';
import {
    UserPlusIcon,
    EnvelopeIcon,
    PhoneIcon,
    TrashIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowUpTrayIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import { EmptyStates } from '../../components/ui/empty-state';
import { useCRM } from '../../contexts/CRMContext';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Leads = () => {
    const { openDialog } = useDialog();
    const { branding } = useBranding();
    const { user, currentAgency } = useCRM();
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [showConvertDialog, setShowConvertDialog] = useState(false);
    const [leadToConvert, setLeadToConvert] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [converting, setConverting] = useState(false);
    const [convertType, setConvertType] = useState('client'); // 'client' or 'account'
    const [clientFormData, setClientFormData] = useState({
        name: '',
        email: '',
        phoneE164: '',
        countryNationality: '',
        countryResidence: '',
        preferredLanguage: 'en',
        timezone: 'UTC',
        status: 'prospect',
        leadSource: 'web_form',
        clientPartnerUserId: '',
        accountId: ''
    });
    const [accountFormData, setAccountFormData] = useState({
        name: '',
        size: '1-5',
        website: '',
        domain: '',
        primaryContact: {
            name: '',
            email: '',
            phoneE164: ''
        },
        address: {
            line1: '',
            city: '',
            country: ''
        },
        ownerUserId: '',
        lifecycle: 'lead',
        segment: 'SMB'
    });
    const isRequestInFlightRef = useRef(false);
    
    // CSV Import state
    const [csvImportStep, setCsvImportStep] = useState(null); // null, 'upload', 'mapping', 'preview', 'importing'
    const [csvFile, setCsvFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [mappedData, setMappedData] = useState([]);
    const [importResults, setImportResults] = useState(null);
    const [audiences, setAudiences] = useState([]);
    const [showAudienceImport, setShowAudienceImport] = useState(false);
    const [selectedAudience, setSelectedAudience] = useState(null);
    const [audiencePreviewData, setAudiencePreviewData] = useState([]);
    const [audienceImporting, setAudienceImporting] = useState(false);
    const [audienceImportResults, setAudienceImportResults] = useState(null);

    useEffect(() => {
        isRequestInFlightRef.current = false;
        loadLeads();
        loadAudiences();
    }, [search, statusFilter, sourceFilter, pagination.page]);
    
    const loadAudiences = async () => {
        try {
            const response = await audiencesAPI.getAll({ limit: 100 });
            if (response.data.success) {
                setAudiences(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load audiences:', error);
        }
    };

    const loadLeads = async () => {
        if (isRequestInFlightRef.current) return;
        
        const token = localStorage.getItem('crm_token');
        if (!token) {
            setLoading(false);
            return;
        }
        
        try {
            isRequestInFlightRef.current = true;
            setLoading(true);
            
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(sourceFilter && { source: sourceFilter })
            };
            
            const response = await leadAPI.getAll(params);
            
            if (!isRequestInFlightRef.current) return;
            
            if (response?.data?.success) {
                setLeads(response.data.data || []);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
            if (error.response?.status === 401) return;
            
            console.error('Failed to load leads:', error);
            toast.error('Failed to load leads');
        } finally {
            isRequestInFlightRef.current = false;
            setLoading(false);
        }
    };

    const handleSelectLead = (leadId) => {
        setSelectedLeads(prev => 
            prev.includes(leadId) 
                ? prev.filter(id => id !== leadId)
                : [...prev, leadId]
        );
    };

    const handleSelectAll = () => {
        if (selectedLeads.length === leads.length) {
            setSelectedLeads([]);
        } else {
            setSelectedLeads(leads.map(lead => lead._id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedLeads.length === 0) return;
        
        openDialog({
            title: 'Delete Leads',
            description: `Are you sure you want to delete ${selectedLeads.length} lead(s)? This action cannot be undone.`,
            confirmText: 'Delete Leads',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    setBulkDeleteLoading(true);
                    await Promise.all(selectedLeads.map(id => leadAPI.delete(id)));
                    toast.success(`${selectedLeads.length} lead(s) deleted successfully`);
                    setSelectedLeads([]);
                    loadLeads();
                } catch (error) {
                    console.error('Error deleting leads:', error);
                    toast.error('Failed to delete leads');
                } finally {
                    setBulkDeleteLoading(false);
                }
            }
        });
    };

    const loadUsersAndAccounts = async () => {
        try {
            setUsersLoading(true);
            setAccountsLoading(true);
            
            const { accountAPI } = await import('../../utils/crmApi');
            const [usersRes, accountsRes] = await Promise.all([
                usersAPI.getAll(),
                accountAPI.getAll({ limit: 1000 }).catch(() => ({ data: { success: true, data: [] } }))
            ]);
            
            if (usersRes.data?.success) {
                setUsers(usersRes.data.data || []);
            }
            
            if (accountsRes.data?.success) {
                setAccounts(accountsRes.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load users/accounts:', error);
            toast.error('Failed to load data');
        } finally {
            setUsersLoading(false);
            setAccountsLoading(false);
        }
    };

    const handleConvertLead = async (leadId) => {
        const lead = leads.find(l => l._id === leadId);
        if (!lead) return;
        
        // Pre-fill client form with lead data
        setClientFormData({
            name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
            email: lead.email || '',
            phoneE164: lead.phoneE164 || lead.phone || '',
            countryNationality: lead.country || '',
            countryResidence: lead.country || '',
            preferredLanguage: lead.preferredLanguage || 'en',
            timezone: lead.timezone || 'UTC',
            status: 'prospect',
            leadSource: lead.source || 'web_form',
            clientPartnerUserId: lead.assignedTo?._id || lead.assignedTo || user?._id || '',
            accountId: ''
        });
        
        // Pre-fill account form with lead data (but keep name blank for user to enter)
        const emailDomain = lead.email?.split('@')[1]?.toLowerCase();
        setAccountFormData({
            name: '', // Keep blank - user should enter company name
            size: '1-5',
            website: '',
            domain: emailDomain || '',
            primaryContact: {
                name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
                email: lead.email || '',
                phoneE164: lead.phoneE164 || lead.phone || ''
            },
            address: {
                line1: '',
                city: lead.city || '',
                country: lead.country || ''
            },
            ownerUserId: lead.assignedTo?._id || lead.assignedTo || user?._id || '',
            lifecycle: 'lead',
            segment: 'SMB'
        });
        
        setConvertType('client'); // Default to client
        setLeadToConvert(lead);
        setShowConvertDialog(true);
        await loadUsersAndAccounts();
    };

    const handleConvertSubmit = async () => {
        if (!leadToConvert) return;
        
        if (convertType === 'account') {
            // Validate account data
            if (!accountFormData.name || !accountFormData.size) {
                toast.error('Account name and size are required');
                return;
            }
            
            if (!accountFormData.ownerUserId) {
                toast.error('Please assign an owner');
                return;
            }
            
            try {
                setConverting(true);
                const response = await leadAPI.convert(leadToConvert._id, {
                    convertType: 'account',
                    accountData: {
                        name: accountFormData.name,
                        size: accountFormData.size,
                        website: accountFormData.website || undefined,
                        domain: accountFormData.domain || undefined,
                        primaryContact: accountFormData.primaryContact,
                        address: accountFormData.address,
                        ownerUserId: accountFormData.ownerUserId,
                        lifecycle: accountFormData.lifecycle,
                        segment: accountFormData.segment
                    }
                });
                
                if (response.data?.success) {
                    toast.success('Lead converted to account successfully!');
                    setShowConvertDialog(false);
                    setLeadToConvert(null);
                    loadLeads();
                    if (response.data.data?.account?._id) {
                        setTimeout(() => {
                            navigate(`/accounts/${response.data.data.account._id}`);
                        }, 500);
                    }
                }
            } catch (error) {
                console.error('Failed to convert lead:', error);
                const errorMessage = error.response?.data?.message || 'Failed to convert lead';
                toast.error(errorMessage);
            } finally {
                setConverting(false);
            }
        } else {
            // Validate client data
            if (!clientFormData.name || !clientFormData.email) {
                toast.error('Name and email are required');
                return;
            }
            
            if (!clientFormData.clientPartnerUserId) {
                toast.error('Please assign a partner/user');
                return;
            }
            
            try {
                setConverting(true);
                const response = await leadAPI.convert(leadToConvert._id, {
                    convertType: 'client',
                    clientData: {
                        name: clientFormData.name,
                        email: clientFormData.email,
                        phoneE164: clientFormData.phoneE164 || undefined,
                        countryNationality: clientFormData.countryNationality || undefined,
                        countryResidence: clientFormData.countryResidence || undefined,
                        preferredLanguage: clientFormData.preferredLanguage,
                        timezone: clientFormData.timezone,
                        status: clientFormData.status,
                        leadSource: clientFormData.leadSource,
                        clientPartnerUserId: clientFormData.clientPartnerUserId,
                        ...(clientFormData.accountId && { accountId: clientFormData.accountId })
                    }
                });
                
                if (response.data?.success) {
                    toast.success('Lead converted to client successfully!');
                    setShowConvertDialog(false);
                    setLeadToConvert(null);
                    loadLeads();
                    if (response.data.data?.client?._id) {
                        setTimeout(() => {
                            navigate(`/clients/${response.data.data.client._id}`);
                        }, 500);
                    }
                }
            } catch (error) {
                console.error('Failed to convert lead:', error);
                const errorMessage = error.response?.data?.message || 'Failed to convert lead';
                toast.error(errorMessage);
            } finally {
                setConverting(false);
            }
        }
    };

    const handleDeleteLead = (leadId, leadName, isConverted = false) => {
        const warningMessage = isConverted 
            ? `Are you sure you want to delete "${leadName}"? This lead has been converted to a client. Deleting it will remove the lead record, but the client will remain. This action cannot be undone.`
            : `Are you sure you want to delete "${leadName}"? This action cannot be undone and all associated data will be permanently removed.`;
        
        openDialog({
            title: 'Delete Lead',
            description: warningMessage,
            confirmText: 'Delete Lead',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await leadAPI.delete(leadId);
                    toast.success('Lead deleted successfully');
                    loadLeads();
                    // Remove from selected if it was selected
                    setSelectedLeads(prev => prev.filter(id => id !== leadId));
                } catch (error) {
                    console.error('Error deleting lead:', error);
                    const errorMessage = error.response?.data?.message || 'Failed to delete lead';
                    toast.error(errorMessage);
                }
            }
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'new': 'bg-blue-100 text-blue-800',
            'contacted': 'bg-yellow-100 text-yellow-800',
            'qualified': 'bg-green-100 text-green-800',
            'nurturing': 'bg-purple-100 text-purple-800',
            'converted': 'bg-emerald-100 text-emerald-800',
            'disqualified': 'bg-gray-100 text-gray-800',
            'lost': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // CSV Parser function (same as AudienceBuilder)
    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length === 0) return { headers: [], data: [] };
        
        const firstLine = lines[0];
        const delimiter = firstLine.includes(',') ? ',' : (firstLine.includes(';') ? ';' : ',');
        
        const headers = firstLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
        
        const data = lines.slice(1).map(line => {
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === delimiter && !inQuotes) {
                    values.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            values.push(current.trim());
            
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || '';
            });
            return row;
        }).filter(row => Object.values(row).some(val => val.trim() !== ''));
        
        return { headers, data };
    };

    // Handle CSV file upload
    const handleCsvFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }
        
        setCsvFile(file);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const { headers, data } = parseCSV(text);
                
                if (headers.length === 0 || data.length === 0) {
                    toast.error('CSV file is empty or invalid');
                    return;
                }
                
                setCsvHeaders(headers);
                setCsvData(data);
                
                // Auto-map common column names
                const autoMapping = {};
                const fieldMappings = {
                    email: ['email', 'e-mail', 'e_mail', 'email address', 'emailaddress'],
                    firstName: ['first name', 'firstname', 'first_name', 'fname', 'given name'],
                    lastName: ['last name', 'lastname', 'last_name', 'lname', 'surname', 'family name'],
                    phone: ['phone', 'telephone', 'tel', 'phone number', 'phonenumber', 'mobile', 'cell'],
                    company: ['company', 'organization', 'org', 'company name', 'companyname'],
                    jobTitle: ['job title', 'jobtitle', 'job_title', 'position', 'title'],
                    country: ['country', 'nationality', 'country code', 'countrycode'],
                    tags: ['tags', 'tag', 'labels', 'label']
                };
                
                headers.forEach(header => {
                    const headerLower = header.toLowerCase().trim();
                    for (const [field, variations] of Object.entries(fieldMappings)) {
                        if (variations.some(v => headerLower.includes(v))) {
                            autoMapping[header] = field;
                            break;
                        }
                    }
                });
                
                setColumnMapping(autoMapping);
                setCsvImportStep('mapping');
            } catch (error) {
                console.error('Error parsing CSV:', error);
                toast.error('Failed to parse CSV file');
            }
        };
        
        reader.readAsText(file);
    };

    // Map columns and preview data
    const handleColumnMapping = () => {
        if (!columnMapping.email) {
            toast.error('Please map at least the Email column');
            return;
        }
        
        // Validate email addresses and check for duplicates
        const emailSet = new Set();
        const mapped = csvData.map((row, index) => {
            const mappedRow = {};
            Object.entries(columnMapping).forEach(([csvColumn, field]) => {
                if (field && row[csvColumn]) {
                    mappedRow[field] = row[csvColumn].trim();
                }
            });
            
            // Validate email format
            if (mappedRow.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(mappedRow.email)) {
                    mappedRow._invalid = `Invalid email format: ${mappedRow.email}`;
                } else if (emailSet.has(mappedRow.email.toLowerCase())) {
                    mappedRow._invalid = `Duplicate email: ${mappedRow.email}`;
                } else {
                    emailSet.add(mappedRow.email.toLowerCase());
                }
            }
            
            return mappedRow;
        }).filter(row => row.email); // Only include rows with email
        
        const invalidRows = mapped.filter(row => row._invalid);
        if (invalidRows.length > 0) {
            toast.error(`${invalidRows.length} row(s) have invalid or duplicate emails. Please fix them.`);
            // Still show preview but mark invalid rows
        }
        
        setMappedData(mapped);
        setCsvImportStep('preview');
    };

    // Import leads from CSV
    const handleImportLeads = async () => {
        if (mappedData.length === 0) {
            toast.error('No valid leads to import');
            return;
        }
        
        // Filter out invalid rows
        const validData = mappedData.filter(row => !row._invalid);
        if (validData.length === 0) {
            toast.error('No valid leads to import after validation');
            return;
        }
        
        try {
            setCsvImportStep('importing');
            setImportResults({ imported: 0, skipped: 0, errors: [] });
            
            const results = { imported: 0, skipped: 0, errors: [] };
            const emailSet = new Set(); // Track emails in this import batch
            
            for (const lead of validData) {
                try {
                    // Check for duplicate in current batch
                    if (emailSet.has(lead.email.toLowerCase())) {
                        results.skipped++;
                        continue;
                    }
                    emailSet.add(lead.email.toLowerCase());
                    
                    // Check if lead exists by email
                    const existingResponse = await leadAPI.getAll({ 
                        search: lead.email,
                        limit: 10 
                    });
                    
                    if (existingResponse.data.success) {
                        const found = existingResponse.data.data.find(l => 
                            l.email?.toLowerCase() === lead.email.toLowerCase()
                        );
                        if (found) {
                            results.skipped++;
                            continue;
                        }
                    }
                    
                    // Create new lead
                    // Format phone to E.164 if provided
                    let phoneE164 = undefined;
                    if (lead.phone) {
                        const phone = lead.phone.trim();
                        if (phone.startsWith('+')) {
                            // Validate E.164 format
                            if (/^\+[1-9]\d{1,14}$/.test(phone)) {
                                phoneE164 = phone;
                            }
                        } else {
                            // Try to convert to E.164
                            const digits = phone.replace(/\D/g, '');
                            if (digits.length >= 10 && digits.length <= 15) {
                                phoneE164 = `+${digits}`;
                            }
                        }
                    }
                    
                    // Format country to 2-letter uppercase code
                    let countryCode = undefined;
                    if (lead.country) {
                        const country = lead.country.trim().toUpperCase();
                        // If it's already 2 letters, use it
                        if (/^[A-Z]{2}$/.test(country)) {
                            countryCode = country;
                        }
                        // Otherwise, try to extract first 2 letters (not ideal but better than nothing)
                        else if (country.length >= 2) {
                            countryCode = country.substring(0, 2);
                        }
                    }
                    
                    const leadData = {
                        email: lead.email, // Required field
                        source: 'other', // Use 'other' since 'csv_import' is not in the enum
                        status: 'new'
                    };
                    
                    // Add optional fields only if they have values
                    if (lead.firstName) leadData.firstName = lead.firstName;
                    if (lead.lastName) leadData.lastName = lead.lastName;
                    if (lead.phone) leadData.phone = lead.phone;
                    if (phoneE164) leadData.phoneE164 = phoneE164;
                    if (lead.company) leadData.company = lead.company;
                    if (lead.jobTitle) leadData.jobTitle = lead.jobTitle;
                    if (countryCode) leadData.country = countryCode;
                    if (lead.tags && lead.tags.length > 0) {
                        leadData.tags = lead.tags.split(',').map(t => t.trim()).filter(t => t);
                    }
                    
                    const createResponse = await leadAPI.create(leadData);
                    
                    if (createResponse.data.success) {
                        results.imported++;
                    } else {
                        throw new Error(createResponse.data.message || 'Failed to create lead');
                    }
                } catch (error) {
                    results.errors.push({
                        email: lead.email,
                        error: error.response?.data?.message || error.message || 'Unknown error'
                    });
                }
            }
            
            setImportResults(results);
            
            if (results.imported > 0) {
                toast.success(`Imported ${results.imported} new lead(s)`);
                loadLeads();
            }
            
            if (results.skipped > 0) {
                toast.error(`${results.skipped} lead(s) skipped (duplicate emails)`);
            }
            
            if (results.errors.length > 0) {
                toast.error(`${results.errors.length} lead(s) failed to import`);
            }
            
            // Close import after a delay
            setTimeout(() => {
                setCsvImportStep(null);
                setCsvFile(null);
                setCsvData([]);
                setCsvHeaders([]);
                setColumnMapping({});
                setMappedData([]);
                setImportResults(null);
            }, 3000);
            
        } catch (error) {
            console.error('Error importing leads:', error);
            toast.error('Failed to import leads');
            setCsvImportStep('preview');
        }
    };

    // Load audience preview
    const handleLoadAudiencePreview = async (audienceId) => {
        try {
            const response = await audiencesAPI.getById(audienceId);
            if (!response.data.success) {
                toast.error('Failed to load audience');
                return;
            }
            
            const audience = response.data.data;
            if (audience.type !== 'static' || !audience.contacts || audience.contacts.length === 0) {
                toast.error('Audience has no contacts to import');
                return;
            }
            
            setSelectedAudience(audience);
            
            // Get client details for preview
            const clientIds = audience.contacts;
            const previewData = [];
            
            for (const clientId of clientIds) {
                try {
                    // Convert clientId to string if it's an object
                    const clientIdStr = typeof clientId === 'object' && clientId._id ? clientId._id : String(clientId);
                    
                    const clientResponse = await clientAPI.getById(clientIdStr);
                    if (!clientResponse.data.success) {
                        previewData.push({
                            email: 'Unknown',
                            name: 'Unknown',
                            phone: '',
                            status: 'error',
                            error: 'Client not found'
                        });
                        continue;
                    }
                    
                    const client = clientResponse.data.data;
                    
                    // Check if lead exists by email
                    const existingResponse = await leadAPI.getAll({ 
                        search: client.email,
                        limit: 10 
                    });
                    
                    let status = 'ready';
                    let error = null;
                    
                    if (existingResponse.data.success) {
                        const found = existingResponse.data.data.find(l => 
                            l.email?.toLowerCase() === client.email?.toLowerCase()
                        );
                        if (found) {
                            status = 'duplicate';
                            error = 'Lead with this email already exists';
                        }
                    }
                    
                    // Use firstName/lastName directly, with fallback to name for backward compatibility
                    const firstName = client.firstName || (client.name ? client.name.split(/\s+/)[0] : '');
                    const lastName = client.lastName || (client.name ? client.name.split(/\s+/).slice(1).join(' ') : '');
                    previewData.push({
                        email: client.email || 'No email',
                        firstName: firstName,
                        lastName: lastName,
                        phone: client.phoneE164 || '',
                        company: '',
                        status: status,
                        error: error
                    });
                } catch (error) {
                    previewData.push({
                        email: 'Unknown',
                        name: 'Unknown',
                        phone: '',
                        status: 'error',
                        error: error.message || 'Failed to load client'
                    });
                }
            }
            
            setAudiencePreviewData(previewData);
        } catch (error) {
            console.error('Error loading audience preview:', error);
            toast.error('Failed to load audience preview');
        }
    };

    // Import leads from audience (after preview confirmation)
    const handleImportFromAudience = async () => {
        if (!selectedAudience || audiencePreviewData.length === 0) {
            toast.error('No data to import');
            return;
        }
        
        try {
            setAudienceImporting(true);
            setAudienceImportResults({ imported: 0, skipped: 0, errors: [] });
            
            const results = { imported: 0, skipped: 0, errors: [] };
            
            for (const previewItem of audiencePreviewData) {
                try {
                    // Skip if duplicate or error
                    if (previewItem.status === 'duplicate' || previewItem.status === 'error') {
                        results.skipped++;
                        continue;
                    }
                    
                    // Create lead from preview data
                    const leadData = {
                        email: previewItem.email,
                        source: 'other', // Use 'other' since 'audience_import' is not in the enum
                        status: 'new'
                    };
                    
                    if (previewItem.firstName) leadData.firstName = previewItem.firstName;
                    if (previewItem.lastName) leadData.lastName = previewItem.lastName;
                    if (previewItem.phone) {
                        // Validate E.164 format
                        if (/^\+[1-9]\d{1,14}$/.test(previewItem.phone)) {
                            leadData.phoneE164 = previewItem.phone;
                        } else {
                            leadData.phone = previewItem.phone;
                        }
                    }
                    if (previewItem.company) leadData.company = previewItem.company;
                    
                    const createResponse = await leadAPI.create(leadData);
                    
                    if (createResponse.data.success) {
                        results.imported++;
                    } else {
                        throw new Error(createResponse.data.message || 'Failed to create lead');
                    }
                } catch (error) {
                    results.errors.push({
                        email: previewItem.email,
                        error: error.response?.data?.message || error.message || 'Unknown error'
                    });
                }
            }
            
            setAudienceImportResults(results);
            
            if (results.imported > 0) {
                toast.success(`Imported ${results.imported} lead(s) from audience`);
                loadLeads();
            }
            
            if (results.skipped > 0) {
                toast.error(`${results.skipped} lead(s) skipped (duplicate emails or errors)`);
            }
            
            if (results.errors.length > 0) {
                toast.error(`${results.errors.length} lead(s) failed to import`);
            }
            
            // Close after a delay
            setTimeout(() => {
                setShowAudienceImport(false);
                setSelectedAudience(null);
                setAudiencePreviewData([]);
                setAudienceImporting(false);
                setAudienceImportResults(null);
            }, 3000);
            
        } catch (error) {
            console.error('Error importing from audience:', error);
            toast.error('Failed to import from audience');
            setAudienceImporting(false);
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Lead', sortable: true },
        { accessor: 'contact', label: 'Contact', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'score', label: 'Score', sortable: true },
        { accessor: 'source', label: 'Source', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Leads</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage and track your leads through the sales pipeline
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedLeads.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            disabled={bulkDeleteLoading}
                            className="inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 hover:opacity-90"
                            style={{ 
                                borderColor: `${branding.accentColor}40`,
                                color: branding.accentColor,
                                backgroundColor: `${branding.accentColor}20`,
                                '--tw-ring-color': branding.accentColor
                            }}
                        >
                            <TrashIcon className="h-5 w-5 mr-2" />
                            {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedLeads.length}`}
                        </button>
                    )}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowAudienceImport(true)}
                            className="inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{
                                borderColor: branding.primaryColor,
                                color: branding.primaryColor,
                                backgroundColor: 'transparent',
                                '--tw-ring-color': branding.primaryColor
                            }}
                        >
                            <UsersIcon className="h-5 w-5 mr-2" />
                            Import from Audience
                        </button>
                        <button
                            onClick={() => setCsvImportStep('upload')}
                            className="inline-flex items-center px-4 py-2 border rounded-lg shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{
                                borderColor: branding.primaryColor,
                                color: branding.primaryColor,
                                backgroundColor: 'transparent',
                                '--tw-ring-color': branding.primaryColor
                            }}
                        >
                            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                            Import from CSV
                        </button>
                    </div>
                    {hasPermission(user, PERMISSIONS.CREATE_CLIENT, currentAgency?._id) && (
                        <Link
                            to="/leads/new"
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{
                                backgroundColor: branding.primaryColor,
                                '--tw-ring-color': branding.primaryColor
                            }}
                        >
                            <UserPlusIcon className="h-5 w-5 mr-2" />
                            Add Lead
                        </Link>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        >
                            <option value="">All Statuses</option>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="qualified">Qualified</option>
                            <option value="nurturing">Nurturing</option>
                            <option value="converted">Converted</option>
                            <option value="disqualified">Disqualified</option>
                            <option value="lost">Lost</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Source</label>
                        <select
                            value={sourceFilter}
                            onChange={(e) => setSourceFilter(e.target.value)}
                            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                        >
                            <option value="">All Sources</option>
                            <option value="web_form">Web Form</option>
                            <option value="referral">Referral</option>
                            <option value="social_media">Social Media</option>
                            <option value="email_campaign">Email Campaign</option>
                            <option value="phone_call">Phone Call</option>
                            <option value="website">Website</option>
                            <option value="search">Search</option>
                            <option value="advertisement">Advertisement</option>
                            <option value="event">Event</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStatusFilter('');
                                setSourceFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded-lg hover:bg-muted"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={leads} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search leads by name, email, or company..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-border">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.length === leads.length && leads.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 border-input rounded focus:ring-2"
                                                    style={{ 
                                                        accentColor: branding.primaryColor,
                                                        '--tw-ring-color': branding.primaryColor
                                                    }}
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: branding.primaryColor }}></div>
                    </div>
                                        </td>
                                    </tr>
                ) : leads.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12">
                                            <div className="text-center">
                                                <UserPlusIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                                <h3 className="text-lg font-medium text-foreground mb-2">No leads found</h3>
                                                <p className="text-sm text-muted-foreground mb-4">
                                                    Get started by adding your first lead
                                                </p>
                                                {hasPermission(user, PERMISSIONS.CREATE_CLIENT, currentAgency?._id) && (
                                                    <Link
                                                        to="/leads/new"
                                                        className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white"
                                                        style={{ backgroundColor: branding.primaryColor }}
                                                    >
                                                        <UserPlusIcon className="h-5 w-5 mr-2" />
                                                        Add Lead
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    leads.map((lead) => (
                                        <TableRow 
                                            key={lead._id}
                                            onClick={() => navigate(`/leads/${lead._id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectLead(lead._id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 border-input rounded focus:ring-2"
                                                    style={{ 
                                                        accentColor: branding.primaryColor,
                                                        '--tw-ring-color': branding.primaryColor 
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <TableAvatar 
                                                        name={`${lead.firstName} ${lead.lastName}`}
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <div className="flex items-center flex-wrap gap-2">
                                                        <span
                                                            className="text-sm font-medium text-foreground"
                                                        >
                                                            {lead.firstName} {lead.lastName}
                                                        </span>
                                                        {lead.convertedToClient && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                                                                Converted
                                                            </span>
                                                        )}
                                                        </div>
                                                        {lead.company && (
                                                            <div className="text-xs text-muted-foreground truncate max-w-[220px] sm:max-w-[300px]">
                                                                {lead.company}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    <div className="flex items-center">
                                                        <EnvelopeIcon className="h-4 w-4 text-muted-foreground mr-2" />
                                                        {hasPermission(user, PERMISSIONS.VIEW_PII, currentAgency?._id) ? lead.email : '•••••••'}
                                                    </div>
                                                    {lead.phone && (
                                                        <div className="flex items-center mt-1">
                                                            <PhoneIcon className="h-4 w-4 text-muted-foreground mr-2" />
                                                            {hasPermission(user, PERMISSIONS.VIEW_PII, currentAgency?._id) ? lead.phone : '•••••••'}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                                                    {lead.status?.charAt(0).toUpperCase() + lead.status?.slice(1) || 'New'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <span className="text-sm font-semibold text-foreground">{lead.score || 0}</span>
                                                    <div className="ml-2 w-16 h-2 bg-muted rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full rounded-full transition-all"
                                                            style={{ 
                                                                width: `${lead.score || 0}%`,
                                                                backgroundColor: lead.score >= 70 ? '#10B981' : lead.score >= 40 ? '#F59E0B' : '#6B7280'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-muted-foreground capitalize">
                                                    {lead.source?.replace('_', ' ') || '—'}
                                                </span>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center space-x-2">
                                                <Link
                                                    to={`/leads/${lead._id}`}
                                                    className="text-sm hover:opacity-80 transition-opacity"
                                                    style={{ color: branding.primaryColor }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    View
                                                </Link>
                                                {!lead.convertedToClient && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleConvertLead(lead._id);
                                                        }}
                                                        className="text-sm hover:opacity-80 transition-opacity flex items-center"
                                                        style={{ color: branding.secondaryColor }}
                                                    >
                                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                                        Convert
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteLead(lead._id, `${lead.firstName} ${lead.lastName}`, lead.convertedToClient);
                                                    }}
                                                    className="text-sm hover:opacity-80 transition-opacity flex items-center text-red-600"
                                                    title="Delete lead"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            </table>
                        </div>
                        </div>

                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                        </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-foreground">
                                        Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>

            {/* Convert Lead Dialog */}
            {showConvertDialog && leadToConvert && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[9999] p-4 overflow-y-auto">
                    <div className="bg-card rounded-lg shadow-xl border border-border max-w-2xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        <div className="p-6 border-b border-border flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-foreground">Convert Lead to Client</h2>
                                <button
                                    onClick={() => {
                                        setShowConvertDialog(false);
                                        setLeadToConvert(null);
                                    }}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                Review and complete the client information. Fields marked with * are required.
                            </p>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            {/* Conversion Type Selector */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Convert To <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => setConvertType('client')}
                                        className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                            convertType === 'client'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-input bg-background text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        <div className="font-semibold">Client</div>
                                        <div className="text-xs mt-1">Individual person or contact</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConvertType('account')}
                                        className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                            convertType === 'account'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-input bg-background text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        <div className="font-semibold">Account</div>
                                        <div className="text-xs mt-1">Company or organization</div>
                                    </button>
                                </div>
                            </div>

                            {convertType === 'client' ? (
                                <>
                                    {/* Client Form - Basic Information */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={clientFormData.name}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={clientFormData.email}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, email: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            value={clientFormData.phoneE164}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, phoneE164: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="+1234567890"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Status
                                        </label>
                                        <select
                                            value={clientFormData.status}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, status: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="prospect">Prospect</option>
                                            <option value="active">Active</option>
                                            <option value="paused">Paused</option>
                                            <option value="churned">Churned</option>
                                            <option value="closed_lost">Closed Lost</option>
                                        </select>
                                    </div>
                                        </div>
                                    </div>

                                    {/* Location Information */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Location Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Country (Nationality)
                                        </label>
                                        <input
                                            type="text"
                                            value={clientFormData.countryNationality}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, countryNationality: e.target.value.toUpperCase() }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="ES"
                                            maxLength={2}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Country (Residence)
                                        </label>
                                        <input
                                            type="text"
                                            value={clientFormData.countryResidence}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, countryResidence: e.target.value.toUpperCase() }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="ES"
                                            maxLength={2}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Preferred Language
                                        </label>
                                        <select
                                            value={clientFormData.preferredLanguage}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, preferredLanguage: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="en">English</option>
                                            <option value="es">Spanish</option>
                                            <option value="fr">French</option>
                                            <option value="de">German</option>
                                            <option value="it">Italian</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Timezone
                                        </label>
                                        <input
                                            type="text"
                                            value={clientFormData.timezone}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, timezone: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            placeholder="Europe/Madrid"
                                        />
                                    </div>
                                        </div>
                                    </div>

                                    {/* Assignment */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Assignment</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Assign To <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={clientFormData.clientPartnerUserId}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, clientPartnerUserId: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a user...</option>
                                            {usersLoading ? (
                                                <option disabled>Loading users...</option>
                                            ) : users.length === 0 ? (
                                                <option disabled>No users available</option>
                                            ) : (
                                                users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name || user.email}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Account (Optional)
                                        </label>
                                        <select
                                            value={clientFormData.accountId}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, accountId: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="">No account</option>
                                            {accountsLoading ? (
                                                <option disabled>Loading accounts...</option>
                                            ) : accounts.length === 0 ? (
                                                <option disabled>No accounts available</option>
                                            ) : (
                                                accounts.map(account => (
                                                    <option key={account._id} value={account._id}>
                                                        {account.name || account.companyName}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Lead Source
                                        </label>
                                        <select
                                            value={clientFormData.leadSource}
                                            onChange={(e) => setClientFormData(prev => ({ ...prev, leadSource: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        >
                                            <option value="web_form">Web Form</option>
                                            <option value="referral">Referral</option>
                                            <option value="social_media">Social Media</option>
                                            <option value="email_campaign">Email Campaign</option>
                                            <option value="phone_call">Phone Call</option>
                                            <option value="walk_in">Walk In</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Account Form */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Account Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={accountFormData.name}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Company Size <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={accountFormData.size}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, size: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                >
                                                    <option value="1-5">1-5 employees</option>
                                                    <option value="5-10">5-10 employees</option>
                                                    <option value="10-50">10-50 employees</option>
                                                    <option value="50-100">50-100 employees</option>
                                                    <option value="100-1000">100-1000 employees</option>
                                                    <option value="1000+">1000+ employees</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Website
                                                </label>
                                                <input
                                                    type="url"
                                                    value={accountFormData.website}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, website: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="https://example.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Domain
                                                </label>
                                                <input
                                                    type="text"
                                                    value={accountFormData.domain}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, domain: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="example.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Primary Contact</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Name
                                                </label>
                                        <input
                                            type="text"
                                            value={accountFormData.primaryContact.name}
                                            onChange={(e) => setAccountFormData(prev => ({
                                                ...prev,
                                                primaryContact: { ...prev.primaryContact, name: e.target.value }
                                            }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                        />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={accountFormData.primaryContact.email}
                                                    onChange={(e) => setAccountFormData(prev => ({
                                                        ...prev,
                                                        primaryContact: { ...prev.primaryContact, email: e.target.value }
                                                    }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={accountFormData.primaryContact.phoneE164}
                                                    onChange={(e) => setAccountFormData(prev => ({
                                                        ...prev,
                                                        primaryContact: { ...prev.primaryContact, phoneE164: e.target.value }
                                                    }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="+1234567890"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Assignment</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Owner <span className="text-red-500">*</span>
                                                </label>
                                        <select
                                            value={accountFormData.ownerUserId}
                                            onChange={(e) => setAccountFormData(prev => ({ ...prev, ownerUserId: e.target.value }))}
                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                            required
                                        >
                                            <option value="">Select a user...</option>
                                            {usersLoading ? (
                                                <option disabled>Loading users...</option>
                                            ) : users.length === 0 ? (
                                                <option disabled>No users available</option>
                                            ) : (
                                                users.map(user => (
                                                    <option key={user._id} value={user._id}>
                                                        {user.name || user.email}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Lifecycle
                                                </label>
                                                <select
                                                    value={accountFormData.lifecycle}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, lifecycle: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="lead">Lead</option>
                                                    <option value="prospect">Prospect</option>
                                                    <option value="customer">Customer</option>
                                                    <option value="churned">Churned</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Segment
                                                </label>
                                                <select
                                                    value={accountFormData.segment}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, segment: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="SMB">SMB</option>
                                                    <option value="Agency">Agency</option>
                                                    <option value="Corp">Corp</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="p-6 border-t border-border flex items-center justify-end space-x-3 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowConvertDialog(false);
                                    setLeadToConvert(null);
                                }}
                                className="px-4 py-2 border border-input rounded-md text-sm font-medium text-foreground hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConvertSubmit}
                                disabled={converting}
                                className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: branding.primaryColor }}
                            >
                                {converting ? 'Converting...' : convertType === 'account' ? 'Convert to Account' : 'Convert to Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSV Import Modal */}
            {csvImportStep && (
                <div 
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && csvImportStep !== 'importing') {
                            setCsvImportStep(null);
                        }
                    }}
                >
                    <div 
                        className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Upload Step */}
                        {csvImportStep === 'upload' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Import Leads from CSV</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <div className="border-2 border-dashed border-input rounded-lg p-12 text-center">
                                    <ArrowUpTrayIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-foreground mb-2">Upload a CSV file with lead information</p>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Supported columns: Email (required), First Name, Last Name, Phone, Company, Job Title, Country, Tags
                                    </p>
                                    <label className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer">
                                        Choose CSV File
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleCsvFileUpload}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Mapping Step */}
                        {csvImportStep === 'mapping' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Map CSV Columns</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Map each CSV column to the corresponding field. Email is required.
                                </p>
                                <div className="space-y-3 mb-4">
                                    {csvHeaders.map(header => (
                                        <div key={header} className="flex items-center gap-4">
                                            <div className="flex-1 text-sm font-medium text-foreground">
                                                {header}
                                            </div>
                                            <select
                                                value={columnMapping[header] || ''}
                                                onChange={(e) => setColumnMapping(prev => ({
                                                    ...prev,
                                                    [header]: e.target.value || undefined
                                                }))}
                                                className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                            >
                                                <option value="">-- Skip this column --</option>
                                                <option value="email">Email *</option>
                                                <option value="firstName">First Name</option>
                                                <option value="lastName">Last Name</option>
                                                <option value="phone">Phone</option>
                                                <option value="company">Company</option>
                                                <option value="jobTitle">Job Title</option>
                                                <option value="country">Country</option>
                                                <option value="tags">Tags (comma-separated)</option>
                                            </select>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setCsvImportStep('upload')}
                                        className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleColumnMapping}
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                                    >
                                        Preview Import
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Preview Step */}
                        {csvImportStep === 'preview' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Preview Import</h3>
                                    <button
                                        onClick={() => setCsvImportStep(null)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {mappedData.filter(r => !r._invalid).length} valid lead{mappedData.filter(r => !r._invalid).length !== 1 ? 's' : ''} ready to import
                                    {mappedData.filter(r => r._invalid).length > 0 && (
                                        <span className="text-destructive ml-2">
                                            ({mappedData.filter(r => r._invalid).length} invalid)
                                        </span>
                                    )}
                                </p>
                                <div className="border border-border rounded-lg overflow-hidden mb-4">
                                    <div className="overflow-x-auto max-h-64">
                                        <table className="min-w-full divide-y divide-border">
                                            <thead className="bg-muted">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Phone</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Company</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-background divide-y divide-border">
                                                {mappedData.slice(0, 10).map((lead, index) => (
                                                    <tr key={index} className={lead._invalid ? 'bg-destructive/10' : ''}>
                                                        <td className="px-3 py-2 text-sm text-foreground">{lead.email}</td>
                                                        <td className="px-3 py-2 text-sm text-foreground">
                                                            {`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-foreground">{lead.phone || '-'}</td>
                                                        <td className="px-3 py-2 text-sm text-foreground">{lead.company || '-'}</td>
                                                        <td className="px-3 py-2 text-sm">
                                                            {lead._invalid ? (
                                                                <span className="text-destructive text-xs">{lead._invalid}</span>
                                                            ) : (
                                                                <span className="text-muted-foreground">Valid</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {mappedData.length > 10 && (
                                        <div className="px-3 py-2 text-xs text-muted-foreground bg-muted">
                                            ... and {mappedData.length - 10} more lead{mappedData.length - 10 !== 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setCsvImportStep('mapping')}
                                        className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleImportLeads}
                                        disabled={mappedData.filter(r => !r._invalid).length === 0}
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        Import Leads
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Importing Step */}
                        {csvImportStep === 'importing' && (
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Importing Leads...</h3>
                                </div>
                                <div className="text-center py-8">
                                    <ArrowPathIcon className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                                    <p className="text-muted-foreground">Please wait while we import your leads</p>
                                </div>
                            </div>
                        )}

                        {/* Results Step */}
                        {csvImportStep === 'importing' && importResults && (
                            <div className="p-6 border-t border-border">
                                <h4 className="font-semibold text-foreground mb-2">Import Results</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="text-green-600">✓ Imported: {importResults.imported}</div>
                                    {importResults.skipped > 0 && (
                                        <div className="text-yellow-600">⚠ Skipped: {importResults.skipped}</div>
                                    )}
                                    {importResults.errors.length > 0 && (
                                        <div className="text-red-600">✗ Errors: {importResults.errors.length}</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Audience Import Modal */}
            {showAudienceImport && (
                <div 
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !audienceImporting) {
                            setShowAudienceImport(false);
                            setSelectedAudience(null);
                            setAudiencePreviewData([]);
                        }
                    }}
                >
                    <div 
                        className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!selectedAudience ? (
                            // Audience Selection Step
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Import Leads from Audience</h3>
                                    <button
                                        onClick={() => {
                                            setShowAudienceImport(false);
                                            setSelectedAudience(null);
                                            setAudiencePreviewData([]);
                                        }}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Select an audience to preview and import contacts as leads. Duplicate emails will be skipped.
                                </p>
                                <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                                    {audiences.filter(a => a.type === 'static' && a.contacts?.length > 0).map(audience => (
                                        <button
                                            key={audience._id}
                                            onClick={() => handleLoadAudiencePreview(audience._id)}
                                            className="w-full text-left p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                                        >
                                            <div className="font-medium text-foreground">{audience.name}</div>
                                            <div className="text-sm text-muted-foreground mt-1">
                                                {audience.contacts.length} contact{audience.contacts.length !== 1 ? 's' : ''}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                {audiences.filter(a => a.type === 'static' && a.contacts?.length > 0).length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No static audiences with contacts available
                                    </div>
                                )}
                            </div>
                        ) : audienceImporting ? (
                            // Importing Step
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Importing Leads...</h3>
                                </div>
                                <div className="text-center py-8">
                                    <ArrowPathIcon className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                                    <p className="text-muted-foreground">Please wait while we import your leads</p>
                                </div>
                                {audienceImportResults && (
                                    <div className="border-t border-border pt-4 mt-4">
                                        <h4 className="font-semibold text-foreground mb-2">Import Results</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="text-green-600">✓ Imported: {audienceImportResults.imported}</div>
                                            {audienceImportResults.skipped > 0 && (
                                                <div className="text-yellow-600">⚠ Skipped: {audienceImportResults.skipped}</div>
                                            )}
                                            {audienceImportResults.errors.length > 0 && (
                                                <div className="text-red-600">✗ Errors: {audienceImportResults.errors.length}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Preview Step
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-foreground">Preview Import</h3>
                                    <button
                                        onClick={() => {
                                            setSelectedAudience(null);
                                            setAudiencePreviewData([]);
                                        }}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <div className="mb-4">
                                    <p className="text-sm text-muted-foreground">
                                        Previewing leads from: <span className="font-medium text-foreground">{selectedAudience.name}</span>
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {audiencePreviewData.filter(p => p.status === 'ready').length} ready to import
                                        {audiencePreviewData.filter(p => p.status === 'duplicate').length > 0 && (
                                            <span className="text-yellow-600 ml-2">
                                                ({audiencePreviewData.filter(p => p.status === 'duplicate').length} duplicates will be skipped)
                                            </span>
                                        )}
                                        {audiencePreviewData.filter(p => p.status === 'error').length > 0 && (
                                            <span className="text-red-600 ml-2">
                                                ({audiencePreviewData.filter(p => p.status === 'error').length} errors)
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div className="border border-border rounded-lg overflow-hidden mb-4">
                                    <div className="overflow-x-auto max-h-96">
                                        <table className="min-w-full divide-y divide-border">
                                            <thead className="bg-muted sticky top-0">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Phone</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-background divide-y divide-border">
                                                {audiencePreviewData.slice(0, 50).map((item, index) => (
                                                    <tr key={index} className={item.status === 'error' ? 'bg-destructive/10' : item.status === 'duplicate' ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}>
                                                        <td className="px-3 py-2 text-sm text-foreground">{item.email}</td>
                                                        <td className="px-3 py-2 text-sm text-foreground">
                                                            {`${item.firstName || ''} ${item.lastName || ''}`.trim() || '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-foreground">{item.phone || '-'}</td>
                                                        <td className="px-3 py-2 text-sm">
                                                            {item.status === 'ready' && (
                                                                <span className="text-green-600">Ready</span>
                                                            )}
                                                            {item.status === 'duplicate' && (
                                                                <span className="text-yellow-600" title={item.error}>Duplicate</span>
                                                            )}
                                                            {item.status === 'error' && (
                                                                <span className="text-red-600 text-xs" title={item.error}>Error</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {audiencePreviewData.length > 50 && (
                                        <div className="px-3 py-2 text-xs text-muted-foreground bg-muted">
                                            ... and {audiencePreviewData.length - 50} more lead{audiencePreviewData.length - 50 !== 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => {
                                            setSelectedAudience(null);
                                            setAudiencePreviewData([]);
                                        }}
                                        className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleImportFromAudience}
                                        disabled={audiencePreviewData.filter(p => p.status === 'ready').length === 0}
                                        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        Import {audiencePreviewData.filter(p => p.status === 'ready').length} Lead{audiencePreviewData.filter(p => p.status === 'ready').length !== 1 ? 's' : ''}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Leads;


